import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { AlertTriangle, Clock, Target } from 'lucide-react';
import Pagination from './Pagination';
const Alerts = () => {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;

    const [clientFilter, setClientFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');
    const [warehouseFilter, setWarehouseFilter] = useState('All');

    useEffect(() => {
        const fetchAlerts = async () => {
            setLoading(true);
            try {
                // Pass the status down to backend unless it's "All"
                const statusArg = statusFilter === 'All' ? undefined : statusFilter;
                const warehouseArg = warehouseFilter === 'All' ? undefined : warehouseFilter;
                const res = await api.inventory.getAlerts(statusArg, warehouseArg).catch(() => null);
                if (res?.data && Array.isArray(res.data)) {
                    setAlerts(res.data);
                } else {
                    setAlerts([]);
                }
            } catch (error) {
                console.error("Alerts error", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAlerts();
    }, [statusFilter, warehouseFilter]);

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const uniqueClients = React.useMemo(() => {
        const clients = new Set(alerts.map(item => item.clientName).filter(Boolean));
        return ['All', ...Array.from(clients).sort()];
    }, [alerts]);

    const filteredAlerts = React.useMemo(() => {
        if (clientFilter === 'All') return alerts;
        return alerts.filter(a => a.clientName === clientFilter);
    }, [alerts, clientFilter]);

    const totalPages = Math.ceil(filteredAlerts.length / itemsPerPage);
    const paginatedAlerts = React.useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredAlerts.slice(start, start + itemsPerPage);
    }, [filteredAlerts, currentPage]);

    // Reset to page 1 when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, warehouseFilter, clientFilter]);

    return (
        <div>
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h1>Inventory Alerts</h1>
                    <p>Actionable intelligence regarding your stock levels.</p>
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    {!loading && (
                        <div style={{ backgroundColor: 'rgba(40, 51, 140, 0.1)', color: 'var(--primary)', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 600 }}>
                            {filteredAlerts.length}{alerts.length === 1000 ? '+' : ''} Alerts Found
                        </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Client</label>
                        <select
                            value={clientFilter}
                            onChange={(e) => setClientFilter(e.target.value)}
                            style={{
                                padding: '0.5rem 2rem 0.5rem 1rem',
                                borderRadius: '6px',
                                border: '1px solid #e2e8f0',
                                backgroundColor: 'white',
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                                outline: 'none'
                            }}
                        >
                            {uniqueClients.map(c => (
                                <option key={c} value={c}>{c === 'All' ? 'All Clients' : c}</option>
                            ))}
                        </select>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Location</label>
                        <select
                            value={warehouseFilter}
                            onChange={(e) => setWarehouseFilter(e.target.value)}
                            style={{
                                padding: '0.5rem 2rem 0.5rem 1rem',
                                borderRadius: '6px',
                                border: '1px solid #e2e8f0',
                                backgroundColor: 'white',
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                                outline: 'none'
                            }}
                        >
                            <option value="All">All Warehouses</option>
                            <option value="V2FyZWhvdXNlOjEyNTMyNw==">Primary</option>
                            <option value="V2FyZWhvdXNlOjEyNTQ3NA==">SC_Ramona</option>
                            <option value="V2FyZWhvdXNlOjEyNTQ3Nw==">SC_DALLAS</option>
                            <option value="V2FyZWhvdXNlOjEyNTQ3OA==">SC_FRESNO</option>
                            <option value="V2FyZWhvdXNlOjEzMTU2Ng==">SC_Dubai</option>
                            <option value="V2FyZWhvdXNlOjEzNDU4Mg==">DRMTLGY</option>
                            <option value="V2FyZWhvdXNlOjEzNTUxNQ==">KITSCH_LA</option>
                        </select>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{
                                padding: '0.5rem 2rem 0.5rem 1rem',
                                borderRadius: '6px',
                                border: '1px solid #e2e8f0',
                                backgroundColor: 'white',
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                                outline: 'none'
                            }}
                        >
                            <option value="All">All Alerts</option>
                            <option value="Warning">Warning (Needs Replenishment)</option>
                            <option value="Out of Stock">Out of Stock</option>
                            <option value="Low Stock">Low Stock</option>
                            <option value="Reorder Point">Reorder Point</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="card">
                {loading ? (
                    <p>Loading active alerts...</p>
                ) : alerts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                        <Target size={48} style={{ opacity: 0.5, marginBottom: '1rem' }} />
                        <h3>No Active Alerts</h3>
                        <p>Your inventory levels are perfectly balanced.</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {paginatedAlerts.map((alert) => (
                            <div key={alert.id} style={{
                                display: 'flex', gap: '1.5rem', padding: '1.5rem',
                                border: '1px solid #e2e8f0', borderRadius: '8px',
                                borderLeft: alert.alert_type === 'Out of Stock' ? '4px solid var(--danger)' : (alert.alert_type === 'Warning' ? '4px solid #f97316' : '4px solid var(--warning)')
                            }}>
                                <div>
                                    <AlertTriangle
                                        size={24}
                                        color={alert.alert_type === 'Out of Stock' ? 'var(--danger)' : (alert.alert_type === 'Warning' ? '#f97316' : 'var(--warning)')}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: 600 }}>{alert.alert_type}</h3>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                                            <Clock size={16} />
                                            <span>{formatDate(alert.created_at)}</span>
                                        </div>
                                    </div>
                                    <p style={{ color: 'var(--text-main)', marginBottom: '0.75rem' }}>{alert.alert_message}</p>
                                    <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                                        <span><strong>Client:</strong> <span style={{ color: 'var(--primary)' }}>{alert.clientName}</span></span>
                                        <span><strong>SKU:</strong> <span style={{ color: 'var(--primary)' }}>{alert.sku}</span></span>
                                        <span><strong>Product:</strong> {alert.product_name}</span>
                                        <span><strong>Location:</strong> {alert.warehouse_name}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && filteredAlerts.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px', marginTop: '1rem' }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAlerts.length)} of {filteredAlerts.length} entries
                        </div>
                            <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Alerts;
